import { Injectable, inject } from '@angular/core';
import { catchError, finalize, tap } from 'rxjs/operators';
import { of, BehaviorSubject, Observable, lastValueFrom } from 'rxjs';
import { CampaignsReceiptAdminApiClient, IReceiptQuery, Receipt, ReceiptQuery, ReceiptResponse } from '../api/api-campaigns.service.generated';
import { UserConfiguration, UserConfigurationUMClient } from '../api/api-user-management.service.generated';
import { AuthService } from 'wecheer-amplify';

@Injectable({
  providedIn: 'root',
})
export class ReceiptDatasourceService {
  private client = inject(CampaignsReceiptAdminApiClient);
  private userConfiguration = inject(UserConfigurationUMClient);
  authService = inject(AuthService);

  receipts: Receipt[] = [];
  filter: IReceiptQuery;
  searchText = ''; // Store search text for highlighting

  pageAvailable = new BehaviorSubject<Receipt[]>([]);
  // Add BehaviorSubject to track checkedColumns changes
  checkedColumnsChange$ = new BehaviorSubject<any[]>([]);
  userConfig: UserConfiguration;
  defaultColumnConfig = [
    // { label: 'Preview', property: 'preview', isVisible: true },
    { label: 'Scan Date', property: 'scanDate', isVisible: true },
    { label: 'User Phone', property: 'userPhone', isVisible: true },
    { label: 'Verify Doc Id', property: 'docId', isVisible: true },
    { label: 'Invoice Date', property: 'invoiceDate', isVisible: true },
    { label: 'Invoice No', property: 'invoiceNumber', isVisible: true },
    { label: 'Reference No', property: 'referenceNumber', isVisible: true },
    { label: 'Vendor', property: 'vendor', isVisible: true },
  { label: 'Campaign Display Name', property: 'campaignName', isVisible: true },
    { label: 'Campaign ID', property: 'campaignId', isVisible: true },
    { label: 'Stage', property: 'stage', isVisible: true },
    {
      label: 'Suspect group',
      property: 'suspectDuplicateGroup',
      isVisible: true,
    },
    { label: 'Tag', property: 'tags', isVisible: true },
    { label: 'Decision', property: 'decision', isVisible: true },
    { label: 'Admin Note', property: 'adminNote', isVisible: true },
    // ----------------- New fields for config -----------------
    { label: 'Reason', property: 'reason', isVisible: true },
    // { label: 'Fraud Decision', property: 'fraudDecision', isVisible: true },
    { label: 'Types', property: 'types', isVisible: true },
    { label: 'Warnings', property: 'warnings', isVisible: true },
  ];
  authUserId: string;
  isLoading: boolean;
  isLoadingDisabled = false; // disabled ngx-datatable internal loader
  isError = false;
  isErrorRetry = false;
  isFirstRequest = true;
  hasMoreItems = true;
  error = '';
  pageLimit = 25;
  total: number;
  defaultCheckedColumns: any[] = [
    // 'preview',
    'scanDate',
    'userPhone',
    'docId',
    'invoiceDate',
    'invoiceNumber',
    'referenceNumber',
    'vendor',
    'campaignName',
    'campaignId',
    'stage',
    'suspectDuplicateGroup',
    'tags',
    'decision',
    'adminNote',
    // ----------------- New fields for config -----------------
    'reason',
    // 'fraudDecision',
    'types',
    'warnings',
  ];
  checkedColumns: any[] = this.defaultCheckedColumns;

  constructor() {
    this.checkedColumnsChange$.next(this.checkedColumns);
  }

  onSearch(f: IReceiptQuery,) {
    this.filter = f;
    this.receipts = [];
    this.total = undefined;
    this.hasMoreItems = true;
    this.isFirstRequest = true;
    lastValueFrom(this.getNextPage());
  }

  // New method for UI pagination
  loadPage(query: IReceiptQuery & { page: number, pageSize: number }): Observable<ReceiptResponse> {
    this.filter = query;
    this.isLoading = true;
    this.isError = false;
    this.error = '';

    const skip = (query.page - 1) * query.pageSize;
    const limit = query.pageSize;

    return this.client.queryReceipts(new ReceiptQuery({
      ...query,
      skip: skip,
      maxItems: limit,
    }))
      .pipe(
        tap((chunk: ReceiptResponse) => {
          if (!chunk || !chunk.receipts) return;

          this.total = chunk.total;
          this.receipts = chunk.receipts || []; // Replace instead of append for pagination
          this.isFirstRequest = false;
          this.pageAvailable.next(chunk.receipts);
        }),
        catchError((error) => {
          this.isError = true;
          this.error = error.message;
          return of(null);
        }),
        finalize(() => {
          this.isLoading = false;
        })
      );
  }

  getNextPage(): Observable<ReceiptResponse> {
    const skip = this.receipts.length;
    const limit = this.pageLimit;

    this.isLoading = true;
    this.isError = false;
    this.error = '';

    return this.client
      .queryReceipts(<ReceiptQuery>{
        ...this.filter,
        skip: skip,
        maxItems: limit,
      })
      .pipe(
        catchError((err) => {
          this.isError = true;
          this.isErrorRetry = true;
          this.error = JSON.stringify(err);
          return of(null);
        }),
        tap((chunk: ReceiptResponse) => {
          if (!chunk || !chunk.receipts) return;

          this.isErrorRetry = false;
          this.receipts = [...this.receipts, ...chunk.receipts];
          this.total = chunk.total;
          this.hasMoreItems = this.receipts.length < this.total;
          this.pageAvailable.next(chunk.receipts);
        }),
        finalize(() => {
          this.isLoading = false;
          this.isFirstRequest = false;
        })
      );
  }

  async initColumnConfig() {
    const user = this.authService.user;
    let userId = user.userId;

    if (userId) {
      await this.applyConfig(userId);
    }
  }

  async applyConfig(userId: string) {
    const userConfig = await lastValueFrom(this.userConfiguration
      .getByUserId(userId));
    this.userConfig = userConfig;
    this.authUserId = userId;

    // Apply default column config
    let newCheckedColumns;
    if (
      !userConfig ||
      !userConfig?.configs?.find((config) => config?.name == 'columnConfig') ||
      userConfig.configs[0].value.length < this.defaultCheckedColumns.length
    ) {
      await this.updateColumnConfig(this.defaultColumnConfig);

      newCheckedColumns = this.defaultCheckedColumns;
    } else {
      newCheckedColumns = userConfig?.configs
        ?.find((config) => config?.name == 'columnConfig')
        ?.value.filter((column) => column.isVisible)
        .map((column) => column.property);
    }

    this.checkedColumns = newCheckedColumns;
    // Emit the change
    this.checkedColumnsChange$.next(this.checkedColumns);
  }

  async updateColumnConfig(updatedColumns: any[]) {
    const userId = this.userConfig?.userId || this.authUserId;
    const existingConfigs = this.userConfig?.configs || [];

    const newConfigs = [
      ...existingConfigs.filter((config) => config.name !== 'columnConfig'),
      { name: 'columnConfig', value: updatedColumns },
    ]

    const userConfigs = UserConfiguration.fromJS({
      userId: userId,
      configs: newConfigs,
    });

    this.checkedColumns = newConfigs
      ?.find((config) => config?.name == 'columnConfig')
      ?.value.filter((column) => column.isVisible)
      .map((column) => column.property);

    // Emit the change
    this.checkedColumnsChange$.next(this.checkedColumns);

    this.userConfig = await lastValueFrom(this.userConfiguration
      .save2(userConfigs));

  }

  // Method to manually trigger column change notification
  notifyColumnChange() {
    this.checkedColumnsChange$.next(this.checkedColumns);
  }

  getDataChart(f: IReceiptQuery): Observable<ReceiptResponse> {
    this.receipts = [];
    return this.client
      .queryReceipts(<ReceiptQuery>{
        ...f
      })
      .pipe(
        catchError((err) => {
          this.isError = true;
          this.isErrorRetry = true;
          this.error = JSON.stringify(err);
          return of(null);
        }),
        tap((chunk: ReceiptResponse) => {
          if (!chunk || !chunk.receipts) return;

          this.isErrorRetry = false;
        }),
        finalize(() => {
          this.isLoading = false;
          this.isFirstRequest = false;
        })
      );
  }
}
